/* eslint-disable jsx-a11y/anchor-is-valid */
 
import React, { ReactElement, useState } from 'react';

import { ButtonPattern, Container, Grid, GridItem } from '@constellation/core';
import { useContent } from '@interstellar/react-app-content';
import { useNavigate } from '@interstellar/react-app-routing';
import NeedHelpWrapper from 'components/needHelpWrapper/NeedHelpWrapper';
import PageHeading from 'components/pageHeading/PageHeading';
import StyledBox from 'components/style/styledBox/StyledBox';
import { Button, Link } from 'components/tagging';
import { useForm } from 'react-hook-form';

import { AboutBusinessContent } from './AboutBusinessContent';
import BusinessInputs from './businessInputs';
import BusinessNotification from './businessNotification';
import { getBusinessDetails } from '../../api';
import {
  BUTTON_EVENTS,
  JOURNEY_STEPS,
} from '../../constants/analytics.constants';
import { Brand } from '../../containers/brandProvider';
import { useGlobalProvider } from '../../context/GlobalContext';
import * as Analytics from '../../utils/analytics';
import {
  MAYFAIR_ACCOUNT_CODE,
  BUSINESS_INVALID_ERROR_CODE,
  HOCA_SORT_CODE_PATTERN,
} from '../../utils/constant';
import { AboutBusinessFieldsType } from '../../utils/types';

function AboutBusiness(): ReactElement {
  const { aboutBusinessContent } = useContent<AboutBusinessContent>();
  const { setGlobalContext } = useGlobalProvider();
  const [isExistSortCode, setIsExistSortCode] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const brand: string = (window as any)?.appConfig?.BRAND_NAME;
  const navigate = useNavigate();
  React.useEffect(() => {
    Analytics.setPageLoadEvent(
      JOURNEY_STEPS.ONLINE_REGISTRATION.ABOUT_THE_BUSINESS,
    );
  }, []);
  const {
    register,
    control,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm<AboutBusinessFieldsType>({
    reValidateMode: 'onSubmit',
  });

  const handleContinue = async (
    submittedData: AboutBusinessFieldsType,
  ): Promise<void> => {
    const { businessName, sortCode, accountNumber } = submittedData;
    const [{ mayfairErrorPagePath, aboutYouPagePath }] = aboutBusinessContent;
    if (brand === Brand.LLOYDS && HOCA_SORT_CODE_PATTERN.test(sortCode)) {
      Analytics.setPageLoadEvent(
        JOURNEY_STEPS.ONLINE_REGISTRATION.ABOUT_THE_BUSINESS_ERROR_HOCA,
      );
      setIsExistSortCode(true);
      return;
    }
    setIsExistSortCode(false);
    const trimmedSortCode = sortCode.replace(/[\s-]/g, '');
    const body = {
      organisationName: businessName,
      sortCode: trimmedSortCode,
      accountNumber,
    };

    if (hasError) {
      setHasError(false);
    }

    try {
      const response = await getBusinessDetails(body);
      const { status, data } = response;
      if (status === 200) {
        setGlobalContext({
          organisationName: data?.organisationName,
          sortCode: data?.sortCode,
          accountNumber: data?.accountNumber,
          partyId: data?.partyId,
        });
        navigate(aboutYouPagePath);
      }
    } catch (err) {
      const error: any = err?.response?.data?.errors?.[0];
      const reasonCode: string = error?.reasonCode;

      if (reasonCode === MAYFAIR_ACCOUNT_CODE) {
        navigate(mayfairErrorPagePath);
      } else if (BUSINESS_INVALID_ERROR_CODE.indexOf(reasonCode) !== -1) {
        Analytics.setPageLoadEvent(
          JOURNEY_STEPS.ONLINE_REGISTRATION.ABOUT_THE_BUSINESS_ERROR,
        );
        setHasError(true);
      } else {
        navigate(aboutBusinessContent[0].technicalErrorPageHref);
      }
    }
  };

  const handleNavigation = (path: string): void => {
    navigate(path);
  };

  return (
    <Container padding="none">
      {aboutBusinessContent?.map(
        ({ serviceName, header, continueBtn, previousLabel, previousHref }) => (
          <React.Fragment key="content">
            <PageHeading serviceName={serviceName} header={header} />
            <Grid alignX="center" alignY="top">
              <GridItem lg={8}>
                <StyledBox>
                  <form onSubmit={handleSubmit(handleContinue)} noValidate>
                    <Grid alignX="center" alignY="center">
                      <BusinessNotification
                        errors={errors}
                        isExistSortCode={isExistSortCode}
                        hasError={hasError}
                      />
                      <BusinessInputs
                        errors={errors}
                        register={register}
                        setValue={setValue}
                        control={control}
                        setIsExistSortCode={setIsExistSortCode}
                      />
                      <GridItem>
                        <ButtonPattern marginBottom="none">
                          <Button
                            iconPosition="right"
                            type="submit"
                            taggingLabel={continueBtn}
                          >
                            {continueBtn}
                          </Button>
                          <Link
                            iconPosition="left"
                            onClick={() => handleNavigation(previousHref)}
                            event={BUTTON_EVENTS.PREVIOUS}
                          >
                            {previousLabel}
                          </Link>
                        </ButtonPattern>
                      </GridItem>
                    </Grid>
                  </form>
                </StyledBox>
              </GridItem>

              <GridItem lg={4}>
                <NeedHelpWrapper />
              </GridItem>
            </Grid>
          </React.Fragment>
        ),
      )}
    </Container>
  );
}

export default AboutBusiness;
